import { useMemo } from 'react';
import { useBrokerIsExternal } from '@/common/hooks/useBrokerIsExternal';
import { usePayment } from '$api-clients/hooks';
import { buildReportingSummaryPerformanceQuery } from '../../helpers';
import { useAllTimeStartDate } from '../../ReportsConfiguration/useReportsConfiguration';
import { usePerformanceReportContext } from '../context/PerformanceReportContext';

type ProbedGoalField = 'salesValueGoal' | 'averageSaleValueGoal';

/**
 * Scopes the probes to the current report view, pinned to a single calendar
 * year: `includePartiesWithoutTransactions` buckets agents with no
 * transactions into the year of `closingDateStart`, so a multi-year range
 * would silently drop the other years' goals. Goals are a calendar-year
 * concept, so `period` is always sent — without it the service omits the goal
 * fields entirely.
 */
function useProbeQuery(viewReportAs: string | undefined) {
  const { filters } = usePerformanceReportContext();
  const allTimeStartDate = useAllTimeStartDate();

  return useMemo(() => {
    const baseQuery = buildReportingSummaryPerformanceQuery(
      filters,
      viewReportAs ?? '',
      allTimeStartDate,
    );
    const pinnedYear =
      typeof filters?.reportingPeriod === 'number'
        ? filters.reportingPeriod
        : new Date().getFullYear();

    return {
      ...baseQuery,
      closingDateStart: `${pinnedYear}-01-01`,
      closingDateEnd: `${pinnedYear}-12-31`,
      period: 'YEARLY' as const,
      groupBy: ['AGENT' as const],
      includePartiesWithoutTransactions: true,
      limit: 1,
      offset: 0,
      sortDirection: 'DESC' as const,
    };
  }, [filters, viewReportAs, allTimeStartDate]);
}

/**
 * Whether any row in scope carries `goalField`, asked by sorting on that same
 * field: the service orders goals `NULLS LAST`, so one row answers it. Sorting
 * and reading the same field here is what keeps the two from drifting apart —
 * a row that leads a sort on one goal field says nothing about the other.
 */
function useGoalProbe(
  goalField: ProbedGoalField,
  query: ReturnType<typeof useProbeQuery>,
  viewReportAs: string | undefined,
) {
  const { data, isLoading, error } = usePayment(
    '/reporting/summary',
    viewReportAs
      ? { params: { query: { ...query, sortBy: goalField } } }
      : null,
  );

  return {
    hasGoal: data?.results?.[0]?.[goalField] != null,
    isLoading,
    errored: error !== undefined,
  };
}

/**
 * Whether anyone in the current report scope has any goal set.
 *
 * Goals are stored per calendar year as two values — a sales volume total and
 * an average sale value — and every goal field the API returns derives from
 * them, so "anyone has a goal" reduces to whether either value exists
 * anywhere in scope.
 *
 * While the probes load, goals are reported unavailable (hide until
 * confirmed). If a probe errors, the broker of record decides the fallback:
 * internal brokerages keep the goal UI, so a transient failure can't strip it
 * from users who do have goals, while external (BYOB) brokerages — who have no
 * way to set goals yet — stay hidden rather than risk showing goal UI that can
 * only ever read "No Goals Set". `isLoading` covers resolving the broker too in
 * that case, since `useBrokerIsExternal` reads external until userinfo lands.
 */
export function useGoalsAvailable({
  viewReportAs,
}: {
  viewReportAs: string | undefined;
}): { goalsAvailable: boolean; isLoading: boolean } {
  const query = useProbeQuery(viewReportAs);
  const salesVolumeProbe = useGoalProbe('salesValueGoal', query, viewReportAs);
  const averageSaleProbe = useGoalProbe(
    'averageSaleValueGoal',
    query,
    viewReportAs,
  );

  const { isExternal: brokerIsExternal, isLoading: brokerIsLoading } =
    useBrokerIsExternal();

  const probesLoading =
    salesVolumeProbe.isLoading || averageSaleProbe.isLoading;
  const errored = salesVolumeProbe.errored || averageSaleProbe.errored;
  const hasGoal = salesVolumeProbe.hasGoal || averageSaleProbe.hasGoal;

  if (probesLoading) {
    return { goalsAvailable: false, isLoading: true };
  }

  if (errored) {
    return { goalsAvailable: !brokerIsExternal, isLoading: brokerIsLoading };
  }

  return { goalsAvailable: hasGoal, isLoading: false };
}
